import { html, nothing } from "lit";
import { formatAgo } from "../format.ts";
import { pathForTab } from "../navigation.ts";
import { formatSessionTokens } from "../presenter.ts";
import type { GatewaySessionRow, SessionsListResult } from "../types.ts";
import { renderSessionsScopeBar, type SessionsScopeBarProps } from "./sessions-scope-bar.ts";

/** Which session key has its kebab menu open (null = none). */
let _kebabOpen: string | null = null;

/** Whether the once-only outside-click dismiss is currently attached. */
let kebabDismissAttached = false;

function onKebabDismissClick() {
  kebabDismissAttached = false;
  closeKebab();
}

function closeKebab() {
  _kebabOpen = null;
  // Detach eagerly. `{once: true}` only self-cleans when the click fires, so
  // a menu dismissed by re-render or Escape would otherwise leave the
  // listener attached — and every render added another one.
  if (kebabDismissAttached && typeof document !== "undefined") {
    kebabDismissAttached = false;
    document.removeEventListener("click", onKebabDismissClick);
  }
}

function toggleKebab(key: string) {
  if (_kebabOpen === key) {
    _kebabOpen = null;
  } else {
    _kebabOpen = key;
  }
}

/** Trigger a browser download of the given rows as a JSON file. */
function exportSessionsJson(rows: GatewaySessionRow[]): void {
  const payload = JSON.stringify({ sessions: rows }, null, 2);
  const blob = new Blob([payload], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `sessions-${Date.now()}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export type SessionsProps = {
  loading: boolean;
  result: SessionsListResult | null;
  error: string | null;
  displayedCount: number;
  loadedCount: number;
  activeMinutes: string;
  limit: string;
  includeGlobal: boolean;
  includeUnknown: boolean;
  searchQuery: string;
  basePath: string;
  scopeBar: SessionsScopeBarProps;
  rows: GatewaySessionRow[];
  onFiltersChange: (next: {
    activeMinutes: string;
    limit: string;
    includeGlobal: boolean;
    includeUnknown: boolean;
    search: string;
  }) => void;
  onRefresh: () => void;
  onPatch: (
    key: string,
    patch: {
      label?: string | null;
      thinkingLevel?: string | null;
      verboseLevel?: string | null;
      reasoningLevel?: string | null;
      sendPolicy?: "allow" | "deny" | null;
    },
  ) => void;
  onDelete: (key: string) => void;
  onDeleteAll: (keys: string[]) => void;
  onViewHistory: (key: string) => void;
};

const THINK_LEVELS = ["", "off", "minimal", "low", "medium", "high", "xhigh"] as const;
const BINARY_THINK_LEVELS = ["", "off", "on"] as const;
const VERBOSE_LEVELS = [
  { value: "", label: "inherit" },
  { value: "off", label: "off (explicit)" },
  { value: "on", label: "on" },
  { value: "full", label: "full" },
] as const;
const REASONING_LEVELS = ["", "off", "on", "stream"] as const;

function normalizeProviderId(provider?: string | null): string {
  if (!provider) {
    return "";
  }
  const normalized = provider.trim().toLowerCase();
  if (normalized === "z.ai" || normalized === "z-ai") {
    return "zai";
  }
  return normalized;
}

function isBinaryThinkingProvider(provider?: string | null): boolean {
  return normalizeProviderId(provider) === "zai";
}

function resolveThinkLevelOptions(provider?: string | null): readonly string[] {
  return isBinaryThinkingProvider(provider) ? BINARY_THINK_LEVELS : THINK_LEVELS;
}

function withCurrentOption(options: readonly string[], current: string): string[] {
  if (!current) {
    return [...options];
  }
  if (options.includes(current)) {
    return [...options];
  }
  return [...options, current];
}

function withCurrentLabeledOption(
  options: readonly { value: string; label: string }[],
  current: string,
): Array<{ value: string; label: string }> {
  if (!current) {
    return [...options];
  }
  if (options.some((option) => option.value === current)) {
    return [...options];
  }
  return [...options, { value: current, label: `${current} (custom)` }];
}

function resolveThinkLevelDisplay(value: string, isBinary: boolean): string {
  if (!isBinary) {
    return value;
  }
  if (!value || value === "off") {
    return value;
  }
  return "on";
}

function resolveThinkLevelPatchValue(value: string, isBinary: boolean): string | null {
  if (!value) {
    return null;
  }
  if (!isBinary) {
    return value;
  }
  if (value === "on") {
    return "low";
  }
  return value;
}

export function renderSessions(props: SessionsProps) {
  // Close the kebab when clicking outside any kebab button.
  //
  // `{once: true}` only self-cleans if the click actually FIRES. If the menu
  // is dismissed by a re-render or by Escape instead, the listener stayed
  // attached with every render adding another one — each retaining `props`.
  // Deduplicate on the handler identity so repeated renders replace rather
  // than stack, and detach when a kebab is already open.
  if (typeof document !== "undefined" && _kebabOpen !== null && !kebabDismissAttached) {
    kebabDismissAttached = true;
    document.addEventListener("click", onKebabDismissClick, { once: true });
  }
  const rows = props.rows;
  return html`
    <section class="card sessions-explorer">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Sessions</div>
          <div class="card-sub">
            Browse all gateway sessions or narrow with the same scope → agent → kind → id navigator
            as Fleet Control.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
          ${
            props.rows.length > 0
              ? html`
                  <button
                    class="btn danger"
                    ?disabled=${props.loading}
                    @click=${() => props.onDeleteAll(props.rows.map((r) => r.key))}
                  >
                    Delete All (${props.rows.length})
                  </button>
                  <button
                    class="btn"
                    ?disabled=${props.loading}
                    @click=${() => exportSessionsJson(props.rows)}
                  >
                    Export JSON
                  </button>
                `
              : nothing
          }
        </div>
      </div>

      <div class="muted" style="margin-top: 8px; font-size: 12px;">
        Showing ${props.displayedCount} of ${props.loadedCount} loaded
      </div>

      ${renderSessionsScopeBar(props.scopeBar)}

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="flex: 1; min-width: 200px;">
          <span>Search</span>
          <input
            .value=${props.searchQuery}
            placeholder="key, label, or session name…"
            @input=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: props.limit,
                includeGlobal: props.includeGlobal,
                includeUnknown: props.includeUnknown,
                search: (e.target as HTMLInputElement).value,
              })}
          />
        </label>
        <label class="field" style="width: 160px;">
          <span>Active within (min)</span>
          <input
            .value=${props.activeMinutes}
            @input=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: (e.target as HTMLInputElement).value,
                limit: props.limit,
                includeGlobal: props.includeGlobal,
                includeUnknown: props.includeUnknown,
                search: props.searchQuery,
              })}
          />
        </label>
        <label class="field" style="width: 100px;">
          <span>Limit</span>
          <input
            .value=${props.limit}
            @input=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: (e.target as HTMLInputElement).value,
                includeGlobal: props.includeGlobal,
                includeUnknown: props.includeUnknown,
                search: props.searchQuery,
              })}
          />
        </label>
        <label class="field checkbox">
          <span>Include global</span>
          <input
            type="checkbox"
            .checked=${props.includeGlobal}
            @change=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: props.limit,
                includeGlobal: (e.target as HTMLInputElement).checked,
                includeUnknown: props.includeUnknown,
                search: props.searchQuery,
              })}
          />
        </label>
        <label class="field checkbox">
          <span>Include unknown</span>
          <input
            type="checkbox"
            .checked=${props.includeUnknown}
            @change=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: props.limit,
                includeGlobal: props.includeGlobal,
                includeUnknown: (e.target as HTMLInputElement).checked,
                search: props.searchQuery,
              })}
          />
        </label>
      </div>

      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
          : nothing
      }

      <div class="muted" style="margin-top: 12px;">
        ${props.result ? `Store: ${props.result.path}` : ""}
      </div>

      <div class="table" style="margin-top: 16px;">
        <div class="table-head">
          <div>Key</div>
          <div>Label</div>
          <div>Kind</div>
          <div>Updated</div>
          <div>Tokens</div>
          <div>Thinking</div>
          <div>Verbose</div>
          <div>Reasoning</div>
          <div>Session Name</div>
          <div>Actions</div>
        </div>
        ${
          rows.length === 0
            ? html`
                <div class="muted">
                  No sessions in this scope. Try <strong>All sessions</strong> or widen the path
                  above.
                </div>
              `
            : rows.map((row) =>
                renderRow(
                  row,
                  props.basePath,
                  props.onPatch,
                  props.onDelete,
                  props.onViewHistory,
                  props.loading,
                ),
              )
        }
      </div>
    </section>
  `;
}

function renderRow(
  row: GatewaySessionRow,
  basePath: string,
  onPatch: SessionsProps["onPatch"],
  onDelete: SessionsProps["onDelete"],
  onViewHistory: SessionsProps["onViewHistory"],
  disabled: boolean,
) {
  const updated = row.updatedAt ? formatAgo(row.updatedAt) : "n/a";
  const rawThinking = row.thinkingLevel ?? "";
  const isBinaryThinking = isBinaryThinkingProvider(row.modelProvider);
  const thinking = resolveThinkLevelDisplay(rawThinking, isBinaryThinking);
  const thinkLevels = withCurrentOption(resolveThinkLevelOptions(row.modelProvider), thinking);
  const verbose = row.verboseLevel ?? "";
  const verboseLevels = withCurrentLabeledOption(VERBOSE_LEVELS, verbose);
  const reasoning = row.reasoningLevel ?? "";
  const reasoningLevels = withCurrentOption(REASONING_LEVELS, reasoning);
  const displayName =
    typeof row.displayName === "string" && row.displayName.trim().length > 0
      ? row.displayName.trim()
      : null;
  const label = typeof row.label === "string" ? row.label.trim() : "";
  const showDisplayName = Boolean(displayName && displayName !== row.key && displayName !== label);
  const canLink = row.kind !== "global";
  const chatUrl = canLink
    ? `${pathForTab("chat", basePath)}?session=${encodeURIComponent(row.key)}`
    : null;

  return html`
    <div class="table-row">
      <div
        class="mono session-key-cell ${row.sendPolicy === "deny" ? "session-key-cell--frozen" : ""}"
      >
        ${canLink ? html`<a href=${chatUrl} class="session-link">${row.key}</a>` : row.key}
        ${row.sendPolicy === "deny" ? html`<span title="Frozen (no new turns)" style="margin-left: 4px; font-size: 11px;">&#10052;</span>` : nothing}
        ${showDisplayName ? html`<span class="muted session-key-display-name">${displayName}</span>` : nothing}
      </div>
      <div>
        <input
          .value=${row.label ?? ""}
          ?disabled=${disabled}
          placeholder="(optional)"
          @change=${(e: Event) => {
            const value = (e.target as HTMLInputElement).value.trim();
            onPatch(row.key, { label: value || null });
          }}
        />
      </div>
      <div>${row.kind}</div>
      <div>${updated}</div>
      <div>${formatSessionTokens(row)}</div>
      <div>
        <select
          ?disabled=${disabled}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            onPatch(row.key, {
              thinkingLevel: resolveThinkLevelPatchValue(value, isBinaryThinking),
            });
          }}
        >
          ${thinkLevels.map(
            (level) =>
              html`<option value=${level} ?selected=${thinking === level}>
                ${level || "inherit"}
              </option>`,
          )}
        </select>
      </div>
      <div>
        <select
          ?disabled=${disabled}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            onPatch(row.key, { verboseLevel: value || null });
          }}
        >
          ${verboseLevels.map(
            (level) =>
              html`<option value=${level.value} ?selected=${verbose === level.value}>
                ${level.label}
              </option>`,
          )}
        </select>
      </div>
      <div>
        <select
          ?disabled=${disabled}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            onPatch(row.key, { reasoningLevel: value || null });
          }}
        >
          ${reasoningLevels.map(
            (level) =>
              html`<option value=${level} ?selected=${reasoning === level}>
                ${level || "inherit"}
              </option>`,
          )}
        </select>
      </div>
      <div>${displayName ?? "—"}</div>
      <div>
        <button class="btn" ?disabled=${disabled} @click=${() => onViewHistory(row.key)}>
          View History
        </button>
        <button class="btn danger" ?disabled=${disabled} @click=${() => onDelete(row.key)}>
          Delete
        </button>
        <div class="sessions-kebab-wrapper" style="display: inline-block; position: relative;">
          <button
            class="btn sessions-kebab-btn"
            aria-label="More actions for ${row.key}"
            ?disabled=${disabled}
            @click=${(e: Event) => {
              e.stopPropagation();
              toggleKebab(row.key);
            }}
          >
            &#8942;
          </button>
          ${
            _kebabOpen === row.key
              ? html`
                  <div class="sessions-kebab-menu" role="menu">
                    <button
                      class="sessions-kebab-item"
                      role="menuitem"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        const newLabel = window.prompt("Rename session label:", row.label ?? "");
                        if (newLabel !== null) {
                          onPatch(row.key, { label: newLabel.trim() || null });
                        }
                        closeKebab();
                      }}
                    >
                      Rename
                    </button>
                    <button
                      class="sessions-kebab-item"
                      role="menuitem"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        const isFrozen = row.sendPolicy === "deny";
                        onPatch(row.key, { sendPolicy: isFrozen ? "allow" : "deny" });
                        closeKebab();
                      }}
                    >
                      ${row.sendPolicy === "deny" ? "Unfreeze" : "Freeze"}
                    </button>
                  </div>
                `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
}
